import React, { useState } from "react";
import { api } from "../../../services/api";
import { useLoadingMessages } from "../useLoadingMessages";

const MIN_CHARS = 40;

type Phase = "description" | "naming" | "pitch";

interface TeamNameStepProps {
  step: number;
  totalSteps: number;
  prompt: string;
  setPrompt: (v: string) => void;
  teamName: string;
  setTeamName: (v: string) => void;
  summary: string;
  setSummary: (v: string) => void;
  onStartLogoGeneration: (name: string, description: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const TeamNameStep: React.FC<TeamNameStepProps> = ({
  step, totalSteps,
  prompt, setPrompt,
  teamName, setTeamName,
  summary, setSummary,
  onStartLogoGeneration,
  onNext, onBack,
}) => {
  const [phase, setPhase] = useState<Phase>(() => (teamName.trim() ? "pitch" : "description"));
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [loadingPitch, setLoadingPitch] = useState(false);
  const [namesError, setNamesError] = useState("");
  const [pitchError, setPitchError] = useState("");
  const [descError, setDescError] = useState("");
  const [nameError, setNameError] = useState("");
  const [checkingDesc, setCheckingDesc] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [checkingSummary, setCheckingSummary] = useState(false);

  const { message: namesMsg, animKey: namesKey } = useLoadingMessages(
    ["Brainstorming names…", "Thinking of something unique…", "Almost there…"],
    loadingNames
  );
  const { message: pitchMsg, animKey: pitchKey } = useLoadingMessages(
    ["Crafting your pitch…", "Writing something compelling…", "Highlighting your strengths…", "Almost ready…"],
    loadingPitch
  );

  const descReady = prompt.trim().length >= MIN_CHARS;
  const truncatedPrompt = prompt.trim().length > 80
    ? prompt.trim().slice(0, 80).trimEnd() + "…"
    : prompt.trim();

  const handleContinueFromDescription = async () => {
    if (!descReady) return;
    setDescError("");
    setCheckingDesc(true);
    try {
      const moderation = await api.moderateText(prompt.trim());
      if (moderation.flagged) {
        setDescError("Please rephrase your description — it can't contain explicit, hateful, or derogatory content.");
        return;
      }
    } catch (err) {
      console.error("Failed to moderate team description:", err);
    } finally {
      setCheckingDesc(false);
    }

    setNamesError("");
    setPitchError("");
    setLoadingNames(true);
    setLoadingPitch(true);
    setPhase("naming");

    api.generateNames(prompt.trim())
      .then(data => setSuggestions(data.team_names || []))
      .catch(() => setNamesError("Couldn't generate names. Please try again."))
      .finally(() => setLoadingNames(false));

    api.generateSummary(prompt.trim())
      .then(data => setSummary(data.summary || ""))
      .catch(() => setPitchError("Couldn't generate a pitch. Please try again."))
      .finally(() => setLoadingPitch(false));
  };

  const handleConfirmName = async () => {
    if (!teamName.trim()) return;
    setNameError("");
    setCheckingName(true);
    try {
      const moderation = await api.moderateText(teamName.trim());
      if (moderation.flagged) {
        setNameError("Please choose a different name — it can't contain explicit, hateful, or derogatory content.");
        return;
      }
    } catch (err) {
      console.error("Failed to moderate team name:", err);
    } finally {
      setCheckingName(false);
    }

    onStartLogoGeneration(teamName, prompt);
    setPhase("pitch");
  };

  const handleContinueFromPitch = async () => {
    if (!summary.trim()) return;
    setSummaryError("");
    setCheckingSummary(true);
    try {
      const moderation = await api.moderateText(summary.trim());
      if (moderation.flagged) {
        setSummaryError("Please rephrase your pitch — it can't contain explicit, hateful, or derogatory content.");
        return;
      }
    } catch (err) {
      console.error("Failed to moderate sponsorship pitch:", err);
    } finally {
      setCheckingSummary(false);
    }
    onNext();
  };

  return (
    <div>
      <div className="bk-progress">
        <div className="bk-pip-row">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`bk-pip ${i + 1 < step ? "bk-pip--done" : i + 1 === step ? "bk-pip--active" : ""}`} />
          ))}
        </div>
        <span className="bk-progress-label">Step {step} of {totalSteps}</span>
      </div>

      <div className="bk-eyebrow">[ 01 / Define ]</div>
      <h1 className="bk-title">Define your team.</h1>

      {/* ── Phase 1: Description ── */}
      <div className={`bk-define-block${phase !== "description" ? " bk-define-block--collapsed" : ""}`}>
        <span className="bk-generate-section-label">Describe your team</span>
        {phase === "description" && (
          <p className="bk-desc" style={{ marginBottom: "0.5rem", marginTop: "0.25rem", fontSize: "0.85rem" }}>
            Tell us who you are, what you play, and where.
          </p>
        )}

        {phase !== "description" && (
          <div className="bk-define-summary-row" style={{ marginTop: "0.5rem" }}>
            <span className="bk-define-summary-check">✓</span>
            <span className="bk-define-summary-text">{truncatedPrompt}</span>
          </div>
        )}

        <div
          className={`bk-define-collapse${phase !== "description" ? " bk-define-collapse--closed" : ""}`}
          aria-hidden={phase !== "description"}
        >
          <div>
            <textarea
              className="bk-textarea"
              style={{ minHeight: "90px", marginTop: "0.5rem", marginBottom: "0.5rem" }}
              placeholder="e.g. Junior doctors who play 5-a-side football on Wednesdays in Clapham"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              disabled={phase !== "description"}
              rows={3}
            />
            <div className={`bk-char-hint${descReady ? " bk-char-hint--ok" : ""}`}>
              {prompt.trim().length} / {MIN_CHARS} characters minimum
            </div>
            {descError && <p className="bk-error" style={{ marginTop: "0.5rem" }}>{descError}</p>}
            <div className="bk-actions" style={{ marginTop: "1.25rem" }}>
              <button className="bk-btn-text" onClick={onBack}>← Home</button>
              <button className="bk-btn" onClick={handleContinueFromDescription} disabled={!descReady || checkingDesc}>
                {checkingDesc ? "Checking…" : "Continue →"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Phase 2: Team name ── */}
      {(phase === "naming" || phase === "pitch") && (
        <div className={`bk-define-block bk-define-reveal${phase === "pitch" ? " bk-define-block--collapsed" : ""}`}>
          <span className="bk-generate-section-label">Team name</span>

          {phase === "pitch" && (
            <div className="bk-define-summary-row" style={{ marginTop: "0.5rem" }}>
              <span className="bk-define-summary-check">✓</span>
              <p className="bk-define-confirmed" style={{ margin: 0 }}>{teamName}</p>
            </div>
          )}

          <div
            className={`bk-define-collapse${phase === "pitch" ? " bk-define-collapse--closed" : ""}`}
            aria-hidden={phase === "pitch"}
          >
            <div>
              {loadingNames ? (
                <p className="bk-loading-status">
                  <span key={namesKey} className="bk-loading-msg">{namesMsg}</span>
                </p>
              ) : namesError ? (
                <p className="bk-error" style={{ marginTop: "0.5rem" }}>{namesError}</p>
              ) : suggestions.length > 0 ? (
                <div className="bk-name-pills" style={{ marginTop: "0.75rem" }}>
                  {suggestions.map(name => (
                    <button
                      key={name}
                      className={`bk-name-pill ${teamName === name ? "bk-name-pill--selected" : ""}`}
                      onClick={() => phase === "naming" && setTeamName(name)}
                      disabled={phase === "pitch"}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              ) : null}

              <input
                type="text"
                className="bk-name-input"
                style={{ marginTop: "1rem" }}
                placeholder="Or type your own…"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                maxLength={40}
                disabled={phase === "pitch"}
              />
              {nameError && <p className="bk-error" style={{ marginTop: "0.5rem" }}>{nameError}</p>}
              <div className="bk-actions" style={{ marginTop: "1.25rem" }}>
                <button
                  className="bk-btn"
                  onClick={handleConfirmName}
                  disabled={!teamName.trim() || loadingNames || checkingName}
                >
                  {checkingName ? "Checking…" : "Confirm Name →"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase 3: Pitch review ── */}
      {phase === "pitch" && (
        <div className="bk-define-block bk-define-reveal">
          <span className="bk-generate-section-label">Sponsorship pitch</span>
          <p className="bk-desc" style={{ marginBottom: "0.5rem", marginTop: "0.25rem", fontSize: "0.85rem" }}>
            AI-generated based on your team. Edit it however you like.
          </p>

          {loadingPitch && (
            <p className="bk-loading-status">
              <span key={pitchKey} className="bk-loading-msg">{pitchMsg}</span>
            </p>
          )}
          {pitchError && !loadingPitch && (
            <p className="bk-error" style={{ marginBottom: "0.5rem" }}>{pitchError}</p>
          )}

          <textarea
            className="bk-summary-edit"
            value={loadingPitch ? "" : summary}
            onChange={e => setSummary(e.target.value)}
            disabled={loadingPitch}
            rows={6}
            placeholder={loadingPitch ? "" : "Your sponsorship pitch will appear here…"}
            style={{ opacity: loadingPitch ? 0.3 : 1 }}
          />
          {summaryError && <p className="bk-error" style={{ marginTop: "0.5rem" }}>{summaryError}</p>}

          <div className="bk-actions" style={{ marginTop: "1.5rem" }}>
            <button className="bk-btn-text" onClick={onBack}>← Home</button>
            <button
              className="bk-btn"
              onClick={handleContinueFromPitch}
              disabled={loadingPitch || !summary.trim() || checkingSummary}
            >
              {checkingSummary ? "Checking…" : "Continue →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamNameStep;
